/**
 * Centralized logging utility for the admin application
 * Provides structured logging with different levels and sanitization
 */

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development'
    this.isProduction = process.env.NODE_ENV === 'production'
    this.logLevel = process.env.VUE_APP_LOG_LEVEL || (this.isDevelopment ? 'debug' : 'info')
    
    this.levels = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    }
  }

  /**
   * Sanitize data by removing sensitive information
   */
  sanitize(data) {
    if (!data || typeof data !== 'object') {
      return data
    }

    const sensitiveKeys = [
      'password',
      'token',
      'secret',
      'key',
      'authorization',
      'auth',
      'credit',
      'card',
      'cvv',
      'ssn',
      'social'
    ]

    const sanitized = JSON.parse(JSON.stringify(data))

    const recursiveSanitize = (obj, path = '') => {
      if (!obj || typeof obj !== 'object') return obj

      Object.keys(obj).forEach(key => {
        const lowerKey = key.toLowerCase()
        const fullPath = path ? `${path}.${key}` : key

        // Check if key contains sensitive information
        if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
          obj[key] = '[REDACTED]'
          return
        }

        // Recursively sanitize nested objects
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          recursiveSanitize(obj[key], fullPath)
        }
      })

      return obj
    }

    return recursiveSanitize(sanitized)
  }

  /**
   * Check if the log level should be output
   */
  shouldLog(level) {
    return this.levels[level] >= this.levels[this.logLevel]
  }

  /**
   * Format log message with timestamp and context
   */
  formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString()
    const sanitizedContext = this.sanitize(context)
    
    return {
      timestamp,
      level: level.toUpperCase(),
      message,
      context: sanitizedContext,
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.getSessionId()
    }
  }

  /**
   * Get or create session ID for tracking
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('admin_session_id')
    if (!sessionId) {
      sessionId = `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem('admin_session_id', sessionId)
    }
    return sessionId
  }

  /**
   * Send logs to external service (in production)
   */
  async sendToLogService(logData) {
    if (!this.isProduction) return

    try {
      // In production, you could send to services like:
      // - Sentry, LogRocket, DataDog, etc.
      // For now, we'll just store in localStorage as backup
      const logs = JSON.parse(localStorage.getItem('admin_logs') || '[]')
      logs.push(logData)
      
      // Keep only last 100 logs in localStorage
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100)
      }
      
      localStorage.setItem('admin_logs', JSON.stringify(logs))
    } catch (error) {
      console.warn('Failed to store log:', error)
    }
  }

  /**
   * Debug level logging
   */
  debug(message, context = {}) {
    if (!this.shouldLog('debug')) return

    const logData = this.formatMessage('debug', message, context)
    
    if (this.isDevelopment) {
      console.log(`🔍 [DEBUG] ${message}`, this.sanitize(context))
    }
    
    this.sendToLogService(logData)
  }

  /**
   * Info level logging
   */
  info(message, context = {}) {
    if (!this.shouldLog('info')) return

    const logData = this.formatMessage('info', message, context)
    
    console.log(`ℹ️ [INFO] ${message}`, this.sanitize(context))
    this.sendToLogService(logData)
  }

  /**
   * Warning level logging
   */
  warn(message, context = {}) {
    if (!this.shouldLog('warn')) return

    const logData = this.formatMessage('warn', message, context)
    
    console.warn(`⚠️ [WARN] ${message}`, this.sanitize(context))
    this.sendToLogService(logData)
  }

  /**
   * Error level logging
   */
  error(message, error = null, context = {}) {
    if (!this.shouldLog('error')) return

    const errorContext = {
      ...context,
      error: error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : null
    }

    const logData = this.formatMessage('error', message, errorContext)
    
    console.error(`🚨 [ERROR] ${message}`, this.sanitize(errorContext))
    this.sendToLogService(logData)
  }

  /**
   * Log user interactions
   */
  userAction(action, details = {}) {
    this.info(`User action: ${action}`, {
      action,
      details: this.sanitize(details),
      timestamp: Date.now()
    })
  }

  /**
   * Log API calls
   */
  apiCall(method, url, payload = null, response = null, duration = null) {
    const context = {
      method,
      url: this.sanitizeUrl(url),
      payload: this.sanitize(payload),
      response: this.sanitize(response),
      duration,
      timestamp: Date.now()
    }

    if (response && response.status >= 400) {
      this.warn(`API Error: ${method} ${url}`, context)
    } else {
      this.debug(`API Call: ${method} ${url}`, context)
    }
  }

  /**
   * Remove sensitive parameters from URL
   */
  sanitizeUrl(url) {
    try {
      const urlObj = new URL(url, window.location.origin)
      const sensitiveParams = ['token', 'key', 'secret', 'password']
      
      sensitiveParams.forEach(param => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]')
        }
      })
      
      return urlObj.toString()
    } catch {
      return url
    }
  }

  /**
   * Log navigation events
   */
  navigation(from, to, context = {}) {
    this.info('Navigation', {
      from: from?.path || from,
      to: to?.path || to,
      context: this.sanitize(context)
    })
  }

  /**
   * Log authentication events
   */
  auth(event, details = {}) {
    this.info(`Auth: ${event}`, {
      event,
      details: this.sanitize(details)
    })
  }

  /**
   * Log performance metrics
   */
  performance(metric, value, context = {}) {
    this.debug(`Performance: ${metric}`, {
      metric,
      value,
      context: this.sanitize(context)
    })
  }

  /**
   * Export logs for debugging
   */
  exportLogs() {
    const logs = JSON.parse(localStorage.getItem('admin_logs') || '[]')
    const dataStr = JSON.stringify(logs, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `admin-logs-${Date.now()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  /**
   * Clear stored logs
   */
  clearLogs() {
    localStorage.removeItem('admin_logs')
    this.info('Logs cleared')
  }
}

// Create singleton instance
const logger = new Logger()

// Global error handler
window.addEventListener('error', (event) => {
  logger.error('Global JavaScript Error', event.error, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  })
})

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled Promise Rejection', event.reason, {
    type: 'unhandledrejection'
  })
})

export default logger